import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { P2 } from "../../components/Paragraph";
import Button from "../../components/Button";
import { ReactComponent as SuccessBadge } from "../../components/badges/successBadge.svg";
import { ReactComponent as StarBadge } from "../../components/badges/starBadge.svg";
import { ReactComponent as FailBadge } from "../../components/badges/failBadge.svg";
import styles from "./dashboardPage.module.css";

// bloom: [remember, understand, apply, analyze, evaluate, create]
// Fills sequentially: level N only starts filling when N-1 is full.
// Levels 5-6 (evaluate, create) left empty for now.
const mockTopics = [
  {
    id: "c1", cycle: "I", name: "Realieji skaičiai",
    sections: [
      { id: "s1", name: "Skaičių aibės", bloom: [100, 100, 100, 75, 0, 0] },
      { id: "s2", name: "Kvadratinė šaknis", bloom: [100, 100, 60, 0, 0, 0] },
      { id: "s3", name: "Veiksmai su realiaisiais skaičiais", bloom: [100, 45, 0, 0, 0, 0] },
      { id: "s4", name: "Kubinė šaknis", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c2", cycle: "II", name: "Konstravimas",
    sections: [
      { id: "s5", name: "Vektoriai", bloom: [100, 100, 80, 0, 0, 0] },
      { id: "s6", name: "Vektorių sudėtis ir atimtis", bloom: [100, 35, 0, 0, 0, 0] },
      { id: "s7", name: "Vektoriaus daugyba iš skaičiaus", bloom: [20, 0, 0, 0, 0, 0] },
      { id: "s8", name: "Objektų braižymas", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s9", name: "Modeliavimas", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c3", cycle: "III", name: "Algebriniai reiškiniai",
    sections: [
      { id: "s10", name: "Raidiniai reiškiniai", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s11", name: "Greitosios daugybos formulės", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s12", name: "Daugianarių skaidymas daugikliais", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c4", cycle: "IV", name: "Plokščiosios figūros",
    sections: [
      { id: "s13", name: "Pitagoro teorema", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s14", name: "Trikampio ir trapecijos vidurio linija", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s15", name: "Lygiašonio trikampio savybės", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c5", cycle: "V", name: "Finansiniai skaičiavimai",
    sections: [
      { id: "s16", name: "Procentinis didinimas arba mažinimas", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s17", name: "Paprastosios ir sudėtinės palūkanos", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c6", cycle: "VI", name: "Tiesiniai ir netiesiniai sąryšiai",
    sections: [
      { id: "s18", name: "Tiesinio sąryšio apibrėžtis", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s19", name: "Grafinis tiesinio sąryšio vaizdavimas", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c7", cycle: "VII", name: "Lygčių sistemos",
    sections: [
      { id: "s20", name: "Lygtis su dviem nežinomaisiais", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s21", name: "Sprendimas grafiniu būdu", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s22", name: "Sprendimas keitimo būdu", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s23", name: "Sprendimas sudėties būdu", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c8", cycle: "VIII", name: "Erdvinės figūros",
    sections: [
      { id: "s24", name: "Prizmės ir piramidės", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s25", name: "Ritinys ir kūgis", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s26", name: "Sfera ir rutulys", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c9", cycle: "IX", name: "Duomenys ir jų interpretavimas",
    sections: [
      { id: "s27", name: "Duomenų skirstymas intervalais", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s28", name: "Histograma ir kvartiliai", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
];

const mockHistory = [
  { id: "h1", topic: "Suprastink tiesinę išraišką...", result: "success" },
  { id: "h2", topic: "Suskliausčiuok 8x + 4x + 1?", result: "assisted" },
  { id: "h3", topic: "Suskliausčiuok 5² - 3a² - 1", result: "success" },
  { id: "h4", topic: "Suskliausčiuok 8·1·k(x - 8)?", result: "assisted" },
  { id: "h5", topic: "Mano draugas Robertas...", result: "success" },
  { id: "h6", topic: "Suskliausčiuok 8·1·k(x - 8)?", result: "gaveup" },
  { id: "h7", topic: "Suprastink -9z² + k(x - 8)?", result: "success" },
  { id: "h8", topic: "Daugyk...", result: "gaveup" },
];

const FINISHED_COUNT = 8;
const READY_COUNT = 16;
const TOTAL = FINISHED_COUNT + READY_COUNT;

const DonutChart = ({ finished, ready, total }) => {
  const radius = 45;
  const strokeWidth = 20;
  const circumference = 2 * Math.PI * radius;
  let offset = circumference * 0.75;

  const slices = [
    { value: finished, color: "#82AABE" },
    { value: ready, color: "#F4C150" },
  ];

  return (
    <div style={{ width: "120px", height: "120px", position: "relative" }}>
      <svg width="120" height="120">
        <circle r={radius} cx="60" cy="60" fill="transparent" stroke="#eee" strokeWidth={strokeWidth} />
        {slices.map((slice, i) => {
          const dash = (slice.value / total) * circumference;
          const el = (
            <circle
              key={i}
              r={radius} cx="60" cy="60"
              fill="transparent"
              stroke={slice.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <h3 style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
      }}>
        {total}
      </h3>
    </div>
  );
};

const bloomColors = [
  "#648C94", // remember — teal (trophy badge)
  "#82AABE", // understand — light blue (donut finished)
  "#C0B87F", // apply — olive (star badge)
  "#F4BC4D", // analyze — gold (trophy accent)
  "#E08A5E", // evaluate — warm orange
  "#F95146", // create — red (fail badge)
];

const bloomKeys = [
  "bloom.remember", "bloom.understand", "bloom.apply",
  "bloom.analyze", "bloom.evaluate", "bloom.create",
];

const BloomBar = ({ bloom, t }) => (
  <div className={styles.bloomRow}>
    {bloom.map((pct, i) => (
      <div key={i} className={styles.bloomSegment} title={`${t(bloomKeys[i])}: ${pct}%`}>
        <div className={styles.bloomBarBg}>
          <div
            className={styles.bloomBarFill}
            style={{ width: `${pct}%`, background: bloomColors[i] }}
          />
        </div>
      </div>
    ))}
  </div>
);

const ResultIcon = ({ result }) => {
  if (result === "success") return <SuccessBadge />;
  if (result === "assisted") return <StarBadge />;
  return <FailBadge />;
};

const Sidebar = ({ t }) => {
  const finishedPct = Math.round((FINISHED_COUNT / TOTAL) * 100);
  const unfinishedPct = 100 - finishedPct;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarInner}>
        <Link to="/mockups" style={{ display: "flex" }}>

          <img src="/logotype.png" alt={t("alt.logo")} className={styles.logo} />
        </Link>

        <div className={styles.classLink}>
          <h4 style={{ margin: 0, fontWeight: 800 }}>8 klasė</h4>
          <div className={styles.menuSpacer} />
        </div>

        <div className={styles.menuLink}>{t("nav.tasks")}</div>
        <div className={styles.menuLink}>{t("nav.lessons")}</div>
        <div className={styles.menuLink}>{t("nav.mastery")}</div>

        <div style={{ flexGrow: 1 }} />

        {/* History widget */}
        <div className={styles.widget}>
          <h4 className={styles.widgetHeader}>{t("nav.history")}</h4>
          {mockHistory.map((item) => (
            <div key={item.id} className={styles.historyLine}>
              <ResultIcon result={item.result} />
              <P2 style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.topic}</P2>
            </div>
          ))}
        </div>

        {/* Statistics widget */}
        <div className={styles.widgetStats}>
          <h4 className={styles.widgetHeader}>{t("nav.statistics")}</h4>
          <P2 style={{ margin: 0 }}>{t("statistics.problemsTackled")}</P2>
          <div className={styles.donutContainer}>
            <DonutChart finished={FINISHED_COUNT} ready={READY_COUNT} total={TOTAL} />
          </div>
          <div className={styles.legendRow}>
            <b style={{ color: "#385654" }}>{finishedPct}%</b>
            <span>{t("statistics.finished")}</span>
          </div>
          <div className={styles.legendRow}>
            <b style={{ color: "#865c08" }}>{unfinishedPct}%</b>
            <span>{t("statistics.unfinished")}</span>
          </div>
        </div>
      </div>
    </aside>
  );
};

const CycleGroup = ({ cycle, t }) => {
  const hasProgress = cycle.sections.some((s) => s.bloom.some((v) => v > 0));
  const [expanded, setExpanded] = useState(hasProgress);
  const avgPct = (bloom) => Math.round(bloom.reduce((a, b) => a + b, 0) / bloom.length);

  return (
    <div className={styles.cycleGroup}>
      <h3
        className={`${styles.cycleName} ${!hasProgress ? styles.cycleNameCollapsible : ""}`}
        onClick={() => !hasProgress && setExpanded((v) => !v)}
      >
        <span>{cycle.cycle}. {cycle.name}</span>
        {!hasProgress && (
          <span className={styles.cycleToggle}>{expanded ? "▾" : "▸"}</span>
        )}
      </h3>
      {expanded &&
        cycle.sections.map((section) => {
          const started = section.bloom.some((v) => v > 0);
          return (
            <div key={section.id} className={styles.topicRow}>
              <div className={started ? styles.topicName : styles.topicNameFull}>
                <P2 style={{ margin: 0 }}>{section.name}</P2>
              </div>
              {started ? (
                <>
                  <div className={styles.topicProgress}>
                    <BloomBar bloom={section.bloom} t={t} />
                  </div>
                  <span className={styles.pct}>{avgPct(section.bloom)}%</span>
                </>
              ) : (
                <span className={styles.zeroPct}>0%</span>
              )}
            </div>
          );
        })}
    </div>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.layout}>
      <Sidebar t={t} />
      <main className={styles.main}>
        <div className={styles.content}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t("nav.problems")}</h2>
            <Button important>{t("chat.solveNextProblem")}</Button>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t("nav.progress")}</h2>
            <div className={styles.bloomLegend}>
              {bloomKeys.map((key, i) => (
                <span key={key} className={styles.bloomLegendItem}>
                  <span className={styles.bloomLegendDot} style={{ background: bloomColors[i] }} />
                  {t(key)}
                </span>
              ))}
            </div>
            <div className={styles.topicList}>
              {mockTopics.map((cycle) => (
                <CycleGroup key={cycle.id} cycle={cycle} t={t} />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
